import { addDays, format, parse } from 'date-fns';
import * as Yup from 'yup';
import { userCanEditCreateDeleteView_CountryFacts } from '../features/dash/views/content/CountryFacts/CountryFactsView';
import { userCanEditCreateDeleteView_FeaturedIngredients } from '../features/dash/views/content/FeaturedIngredients/FeaturedIngredientsView';
import { userCanEditCreateDeleteView_FoodHistories } from '../features/dash/views/content/FoodHistories/FoodHistoriesView';
import { userCanEditCreateDeleteView_JokeCategories } from '../features/dash/views/content/JokeCategories/JokeCategoriesView';
import { userCanEditCreateDeleteView_Joke } from '../features/dash/views/content/Jokes/JokesView';
import { userCanEditCreateDeleteView_Questions } from '../features/dash/views/content/Questions/QuestionsView';
import { userCanView_Recipes } from '../features/dash/views/content/Recipes/RecipesView';
// import { userCanView_CurrentEntries } from '../features/dash/views/finance/CurrentEntries/CurrentEntriesView';
import { userCanAdmin_Expenses } from '../features/dash/views/finance/Expenses/ExpensesView';
import { userCanAdmin_Invoices } from '../features/dash/views/finance/Invoices/InvoicesView';
import { userCanViewMasterTimesheet } from '../features/dash/views/finance/MasterTimesheetView';
import { userCanViewEdit_Payments } from '../features/dash/views/finance/Payments/PaymentsView';
import { userCanAdmin_Receipts } from '../features/dash/views/finance/Receipts/ReceiptsView';
import { userCanAdmin_Discounts } from '../features/dash/views/orders/Discounts/DiscountsView';
import { userCanViewRemoveSubmit_Orders } from '../features/dash/views/orders/OrdersView';
import { userCanView_PromotionalCodes } from '../features/dash/views/orders/PromotionalCodes/PromotionalCodesView';
import { userCanAdmin_Refunds } from '../features/dash/views/orders/Refunds/RefundsView';
import { userCanView_Scholarships } from '../features/dash/views/orders/Scholarships/ScholarshipsView';
import { userCanView_AdminUser } from '../features/dash/views/people/AdminsView';
import { userCanView_Allergies } from '../features/dash/views/people/Allergy/AllergiesView';
import { userCanView_Children } from '../features/dash/views/people/Children/ChildrenView';
import { userCanView_Instructors } from '../features/dash/views/people/InstructorsView';
import { userCanView_ParentUser } from '../features/dash/views/people/ParentsView';
import { userCanView_Waviers } from '../features/dash/views/people/Waiver/WaiversView';
import { userCanView_Classes } from '../features/dash/views/schedule/classes/ClassesView';
import { userCanView_NeedSubstitute } from '../features/dash/views/schedule/needs/NeedSubstituteView';
import { userCanView_Needs } from '../features/dash/views/schedule/needs/NeedsView';
import { userCanView_Sessions } from '../features/dash/views/schedule/sessions/SessionsView';
import { userCanEditCreateDeleteView_Waitlist } from '../features/dash/views/schedule/waitlist/WaitlistView';
import { userCanView_TrainingVideos } from '../features/dash/views/training/TrainingVideo/TrainingVideosView';
import { userCanView_Feedbacks } from '../features/dash/views/website/Feedbacks/FeedbacksView';
import { userCanView_Galleries } from '../features/dash/views/website/Galleries/GalleriesView';
import { userCanView_Snippets } from '../features/dash/views/website/Snippets/SnippetsView';
import { userCanView_TeamProfiles } from '../features/dash/views/website/TeamProfiles/TeamProfilesView';
import { User } from '../services/endpoints/people/user';
import { MenuOption } from '../services/helper';
import { Course } from '../types/course.type';
import { Lesson } from '../types/lesson.type';
import { LessonPlan, Recipe } from '../types/lessonPlan.type';
import { Venue } from '../types/venue.type';
import {isLocal, userTypes} from './consts';
import {
  canViewKitchenSkills,
  canViewNewsItems,
  canViewOrganizations,
  canViewPages,
  canViewThemes,
  canViewVenues
} from './rbac';

export const getPartition = (id?: number): string => {
  if (!id) return '_';
  let id_partition = String(id).padStart(9, '0');
  return [id_partition.slice(0, 3), id_partition.slice(3, 6), id_partition.slice(6, 9)].join('/');
};

export const getKeyByValue = <T, K extends keyof T>(obj: T, value: T[K]): K | undefined => {
  const entry = Object.entries(obj!).find(([key, val]) => val === value);
  return entry ? (entry[0] as K) : undefined;
};

export const getNestedTouchedTrue = (schema: Yup.ObjectSchema<any>) => {
  let s = schema.getDefault();
  Object.keys(s).forEach((k) => {
    s[k] = true;
  });
  return s;
};

export const getObjectDifference = (obj_1: any, obj_2: any) => {
  let obj1 = JSON.parse(JSON.stringify(obj_1));
  let obj2 = JSON.parse(JSON.stringify(obj_2));
  var result: { [key: string | number]: any } = {};
  let jsonEqual = JSON.stringify(obj1) === JSON.stringify(obj2);

  if (jsonEqual) {
    return result;
  }
  for (var key in obj1) {
    if (obj2[key] !== obj1[key]) result[key] = obj2[key];
    if (Array.isArray(obj2[key]) && Array.isArray(obj1[key])) {
      let filtered = obj1[key].reduce((prev: any[], curr: any, index: number) => {
        let foundIdx = prev.findIndex((i: any) => i.id === curr.id);
        if (foundIdx !== -1) {
          let found = prev[foundIdx];
          if (JSON.stringify(found) === JSON.stringify(curr)) {
            prev.splice(foundIdx, 1);
          }
        } else {
          if (curr.id) {
            prev.push({ id: curr.id, destroy: true });
          }
        }
        return prev;
      }, obj2[key]);
      result[key] = filtered;
      if (result[key].length === 0) {
        delete result[key];
      }
    } else if (typeof obj2[key] == 'object' && typeof obj1[key] == 'object') {
      var newObj = getObjectDifference(obj1[key], obj2[key]);
      result[key] = newObj;
      if (result[key]) {
        if (Object.keys(result[key]).length === 0) {
          delete result[key];
        }
      }
    }
  }
  return result;
};

export const getErrorMessage = (error: any) => {
  if (!error) return 'Unknown error';

  // Handle RTK Query error shape: { data: { error: ["msg"] } } or { data: { error: "msg" } }
  if (error?.data?.error) {
    if (Array.isArray(error.data.error)) return error.data.error.join(', ');
    if (typeof error.data.error === 'string') return error.data.error;
  }

  // Handle RTK Query error shape: { data: { message: "msg" } }
  if (error?.data?.message) return error.data.message;

  return (
    error.response?.data?.message ||
    error?.message ||
    error?.error ||
    (typeof error === 'string' ? error : null) ||
    'Unknown error'
  );
};

export const formatTimeToAMPM = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const adjustedHours = hours % 12 || 12;
  return `${adjustedHours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`;
};

const FORM_DAY_ABBREVIATIONS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const FORM_DAY_ORDER = [...FORM_DAY_ABBREVIATIONS] as const;
const FULL_DAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
] as const;

const DAY_TOKEN_SPLIT = /,\s*|\s+&\s+|\s+and\s+/i;

function parseDayNameToIndex(token: string): number {
  const lower = token.trim().toLowerCase().replace(/s$/, '');
  if (!lower) return -1;

  const byAbbr = FORM_DAY_ABBREVIATIONS.findIndex(
    (abbr) => abbr.toLowerCase() === lower.slice(0, 3)
  );
  if (byAbbr >= 0) return byAbbr;

  return FULL_DAY_NAMES.findIndex(
    (name) => lower === name || name.startsWith(lower) || lower.startsWith(name)
  );
}

function expandDayRange(startToken: string, endToken: string): string[] {
  const startIdx = parseDayNameToIndex(startToken);
  const endIdx = parseDayNameToIndex(endToken);
  if (startIdx < 0 || endIdx < 0 || startIdx > endIdx) return [];

  return FORM_DAY_ORDER.slice(startIdx, endIdx + 1);
}

/** Parse stored course day_of_week (e.g. "Mondays, Wednesdays") into form abbreviations. */
export const parseStoredDayOfWeek = (stored: string): string[] => {
  if (!stored?.trim()) return [];

  const trimmed = stored.trim();
  const rangeMatch = trimmed.match(/^(.+?)\s*(?:through|to|-)\s*(.+)$/i);
  if (rangeMatch) {
    const rangeDays = expandDayRange(rangeMatch[1], rangeMatch[2]);
    if (rangeDays.length) return rangeDays;
  }

  const parsed: string[] = [];

  for (const part of trimmed.split(DAY_TOKEN_SPLIT)) {
    const token = part.trim();
    if (!token) continue;

    const dayIndex = parseDayNameToIndex(token);
    if (dayIndex < 0) continue;

    const abbr = FORM_DAY_ABBREVIATIONS[dayIndex];
    if (!parsed.includes(abbr)) parsed.push(abbr);
  }

  return parsed;
};

/** Merge day abbreviations in Sun–Sat order. */
export const mergeFormDayAbbreviations = (...groups: string[][]): string[] => {
  const selected = new Set(groups.flat());
  return FORM_DAY_ORDER.filter((day) => selected.has(day));
};

export const convertDaysToFull = (days: string[], startDate: string, endDate: string): string => {
  const dayMap: { [key: string]: string } = {
    Sun: 'Sunday',
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday'
  };

  const fullDays = days.map((day, index) => {
    const fullDayName = dayMap[day];
    const dayCount = countDayOccurrences(index, startDate, endDate);

    return dayCount > 1 ? `${fullDayName}s` : fullDayName;
  });

  return fullDays.join(', ');
};

export const countDayOccurrences = (day: number, startDate: string, endDate: string): number => {
  let count = 0;
  let currentDate = parse(startDate, 'yyyy-MM-dd', new Date());
  const end = parse(endDate, 'yyyy-MM-dd', new Date());
  while (currentDate <= end) {
    if (currentDate.getDay() === day) {
      count++;
    }
    currentDate = addDays(currentDate, 1);
  }

  return count;
};

export const formatDateString = (date: string): string => {
  let d = String(date);
  d = d.includes('T') ? d.split('T')[0] : d;
  d = d.includes('-') ? d : '0000-00-00';
  let dateArr = d.split('-');
  if (dateArr.length !== 3) return date;
  const [year, month, day] = dateArr.map(Number);
  return `${month}/${day}/${year}`;
};

export const formatUrl = (path: string, params: any): string => {
  if (typeof params !== 'object') {
    return path;
  }

  let url = path;
  const { scope, entries } = params;
  const searchParams = new URLSearchParams({
    scope: scope || '',
    entries: entries ? String(entries) : ''
  });
  if (searchParams.toString()) {
    url += '?' + searchParams.toString();
  }
  return url;
};

export const formatTwoDates = (startDateString?: string, endDateString?: string) => {
  if (!startDateString || !endDateString) {
    return 'Invalid date(s)';
  }

  const startDateOnly = startDateString.includes('T') ? startDateString.split('T')[0] : startDateString;
  const endDateOnly = endDateString.includes('T') ? endDateString.split('T')[0] : endDateString;

  // Parse as local calendar dates — `new Date('yyyy-MM-dd')` is UTC midnight and shifts a day in US timezones.
  const startDate = parse(startDateOnly, 'yyyy-MM-dd', new Date());
  const endDate = parse(endDateOnly, 'yyyy-MM-dd', new Date());

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 'Invalid date(s)';
  }

  const formattedStartDate = format(startDate, 'EEEE, MMMM d');
  const formattedEndMonthDay = format(endDate, 'MMMM d');
  const formattedEndYear = format(endDate, 'yyyy');

  return `${formattedStartDate} - ${formattedEndMonthDay}, ${formattedEndYear}`;
};

export const formatAddress = (venue?: Venue): string => {
  if (!venue) {
    return '';
  }

  return `${venue.address}, ${venue.city}, ${venue.state} ${venue.zip}`;
};

export const getClassesRemaining = (course: Course): Lesson[] => {
  if (!course.lessons?.length) return [];
  const tz = course.venue?.location?.timezone ?? 'US/Mountain';
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
  return course.lessons.filter((lesson) => String(lesson.date) >= today);
};

export const formatToCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const getRangeDate = (startDate: Date): { start: string; end: string } => {
  const start = startDate.toISOString().slice(0, 10);
  const end = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0)
    .toISOString()
    .slice(0, 10);
  return { start, end };
};

export const splitArray = (arr: any[], chunkSize: number) => {
  const result = [];

  // Loop over the array and slice it into chunks of chunkSize
  for (let i = 0; i < arr.length; i += chunkSize) {
    result.push(arr.slice(i, i + chunkSize));
  }

  return result;
};

export const stripHtmlTags = (html: string) => {
  let doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const generateFileUrlSegment = (id: number = 0, moduleString: string, size?: string) => {
  const reverseString = (str: string) => {
    return str.split('').reverse().join('');
  };

  let idAddress = id.toString();

  const idLength = idAddress.length;
  for (let i = 0; i < 9 - idLength; i++) {
    idAddress = '0' + idAddress;
  }

  idAddress = reverseString(idAddress);
  idAddress = idAddress.slice(0, 3) + '/' + idAddress.slice(3, 6) + '/' + idAddress.slice(6, 9);
  idAddress = reverseString(idAddress);

  return `${moduleString}/${idAddress}${size ? `/${size}` : ''}`;
};

export const getPrimaryRecipe = (lessonPlan: LessonPlan): Recipe | null => {
  const findPrimaryRecipe = lessonPlan.recipes?.find(
      (recipe) => recipe.lesson_plan_recipes?.is_primary
  );
  return findPrimaryRecipe || null;
};

export const sortLessonPlanRecipes = (recipes: Recipe[]): Recipe[] =>
  [...recipes].sort(
    (a, b) =>
      (b.lesson_plan_recipes?.is_primary ? 1 : 0) - (a.lesson_plan_recipes?.is_primary ? 1 : 0)
  );

export const getLessonPlanRecipeListTitle = (
  recipes: Recipe[],
  separator = ' + '
): string =>
  sortLessonPlanRecipes(recipes)
    .map((recipe) => recipe.title)
    .filter(Boolean)
    .join(separator);

const parseEquipmentField = (field?: string): string[] =>
  field?.split(/\r?\n/).map((name) => name.trim()).filter(Boolean) ?? [];

const getRecipeEquipmentNames = (recipe: Recipe, forInstructor: boolean): string[] => {
  const fromAssociation = forInstructor
    ? (recipe.instructor_equipments ?? []).map((e) => e.name)
    : (recipe.parent_equipments ?? []).map((e) => e.name);
  if (fromAssociation.length) {
    return fromAssociation.filter(Boolean);
  }

  const field = forInstructor
    ? recipe.instructor_equipments_field
    : recipe.parent_equipments_field;
  return parseEquipmentField(field);
};

export const getLessonPlanEquipmentNames = (
  recipes: Recipe[],
  forInstructor: boolean
): string[] => {
  const seen = new Set<string>();
  const equipment: string[] = [];

  sortLessonPlanRecipes(recipes).forEach((recipe) => {
    getRecipeEquipmentNames(recipe, forInstructor).forEach((name) => {
      if (!seen.has(name)) {
        seen.add(name);
        equipment.push(name);
      }
    });
  });

  return equipment;
};

export const getRecipeImage = (recipe: Recipe, size?: string): string | null => {
  const image = recipe.recipe_images?.[0];
  if (!image) return null;
  return `${generateFileUrlSegment(image.id, image.s3_base_url, size)}/${image.image_file_name}`;
};

export const parseRecipeSteps = (steps: string): { title: string; content: string }[] => {
  if (!steps?.trim()) return [];

  const lines = steps.split('\n');
  const items: { title: string; content: string }[] = [];
  let i = 0;

  while (i < lines.length) {
    const titleMatch = lines[i].trim().match(/^\[(.*)\]$/);
    if (titleMatch) {
      const title = titleMatch[1].trim();
      i++;
      const contentLines: string[] = [];
      while (i < lines.length && !lines[i].trim().match(/^\[.*\]$/)) {
        contentLines.push(lines[i]);
        i++;
      }
      items.push({
        title: title || 'Untitled',
        content: contentLines.join('\n').trim()
      });
    } else {
      i++;
    }
  }

  return items;
};

export const getAllowedTabsContent = (user: User | null) => {
  let contentTabs: MenuOption[] = [
    { title: 'Lesson Plans', url: '/admin/content/lesson-plans' },
    { title: 'Documents', url: '/admin/content/documents' }
  ];

  if (user?.user_type_id === userTypes.global_admin) {
    contentTabs.push({ title: 'Videos', url: '/admin/content/videos' });
  }

  if (userCanView_Recipes(user)) {
    contentTabs.push({ title: 'Recipes', url: '/admin/content/recipes' });
  }

  if (canViewThemes(user)) {
    contentTabs.push({ title: 'Themes', url: '/admin/content/themes' });
  }

  if (userCanEditCreateDeleteView_JokeCategories(user)) {
    contentTabs.push({
      title: 'Joke Categories',
      url: '/admin/content/joke-categories'
    });
  }

  if (userCanEditCreateDeleteView_Joke(user)) {
    contentTabs.push({ title: 'Jokes', url: '/admin/content/jokes' });
  }

  if (userCanEditCreateDeleteView_FeaturedIngredients(user)) {
    contentTabs.push({
      title: 'Featured Ingredients',
      url: '/admin/content/featured-ingredients'
    });
  }

  if (userCanEditCreateDeleteView_CountryFacts(user)) {
    contentTabs.push({
      title: 'Country Facts',
      url: '/admin/content/country-facts'
    });
  }

  if (userCanEditCreateDeleteView_FoodHistories(user)) {
    contentTabs.push({
      title: 'Food Histories',
      url: '/admin/content/food-histories'
    });
  }

  if (canViewKitchenSkills(user)) {
    contentTabs.push({
      title: 'Kitchen Skills',
      url: '/admin/content/kitchen-skills'
    });
  }

  if (userCanEditCreateDeleteView_Questions(user)) {
    contentTabs.push({
      title: 'Questions',
      url: '/admin/content/questions'
    });
  }
  return contentTabs;
};

export const getAllowedTabsPlaces = (user: User | null) => {
  let placesTabs: MenuOption[] = [
    {
      title: 'Locations',
      url: '/admin/places/locations'
    },
    {
      title: 'Territories',
      url: '/admin/places/territories'
    },
    {
      title: 'Zip Codes',
      url: '/admin/places/zip-codes'
    }
  ];

  if (canViewVenues(user)) {
    placesTabs.push({
      title: 'Venues',
      url: '/admin/places/venues'
    });
  }

  if (canViewOrganizations(user)) {
    placesTabs.push({
      title: 'Organizations',
      url: '/admin/places/organizations'
    });
  }
  return placesTabs;
};

export const getAllowedTabsTraining = (user: User | null) => {
  let trainingTabs: MenuOption[] = [
    // {
    //   title: "Guides",
    //   url: "/admin/training/guides",
    // },
    // {
    //   title: "Steps",
    //   url: "/admin/training/steps",
    // },
  ];

  if (userCanView_TrainingVideos(user)) {
    trainingTabs.push({
      title: 'Videos',
      url: '/admin/training/videos'
    });
  }
  return trainingTabs;
};

export const getAllowedTabsWebsite = (user: User | null) => {
  let websiteTabs: MenuOption[] = [
    // {
    //   title: 'Franchisee Leads',
    //   url: '/admin/website/franchisee-leads'
    // },
  ];

  if (canViewNewsItems(user)) {
    websiteTabs.push({
      title: 'News Items',
      url: '/admin/website/news-items'
    });
  }

  if (canViewPages(user)) {
    websiteTabs.push({
      title: 'Pages',
      url: '/admin/website/pages'
    });
  }

  if (userCanView_Snippets(user)) {
    websiteTabs.push({
      title: 'Snippets',
      url: '/admin/website/snippets'
    });
  }

  if (userCanView_TeamProfiles(user)) {
    websiteTabs.push({
      title: 'Team Profiles',
      url: '/admin/website/team-profiles'
    });
  }

  if (userCanView_Feedbacks(user)) {
    websiteTabs.push({
      title: 'Feedbacks',
      url: '/admin/website/feedbacks'
    });
  }

  if (userCanView_Galleries(user)) {
    websiteTabs.push({
      title: 'Galleries',
      url: '/admin/website/galleries'
    });
  }

  return websiteTabs;
};

export const getAllowedTabsSchedule_SessionsClasses = (user: User | null) => {
  let sessionsTabs: MenuOption[] = [];

  if (userCanView_Sessions(user)) {
    sessionsTabs.push({
      title: 'Sessions',
      url: '/admin/schedule/sessions'
    });
  }

  if (userCanView_Classes(user)) {
    sessionsTabs.push({
      title: 'Classes',
      url: '/admin/schedule/classes'
    });
  }

  if (userCanEditCreateDeleteView_Waitlist(user)) {
    sessionsTabs.push({
      title: 'Waitlists',
      url: '/admin/schedule/waitlists'
    });
  }
  return sessionsTabs;
};

export const getAllowedTabsSchedule_ScheduleNeeds = (user: User | null) => {
  let scheduleTabs: MenuOption[] = [];
  // Instructor Session Requests is admin-intended; hidden from instructors.
  if (
      user?.isSuper ||
      user?.isSupremeAdmin ||
      user?.isAdmin ||
      user?.isGlobal ||
      user?.isLocationAdmin
  ) {
    scheduleTabs.push({
      title: 'Instructor Session Requests',
      url: '/admin/schedule-needs/instructor-session-requests'
    });
  }

  if (userCanView_Needs(user)) {
    scheduleTabs.push({
      title: 'Instructor Needed',
      url: '/admin/schedule-needs/need-instructor'
    });
  }

  if (userCanView_NeedSubstitute(user)) {
    scheduleTabs.push({
      title: 'Subs Needed',
      url: '/admin/schedule-needs/need-substitute'
    });
  }
  return scheduleTabs;
};

export const getAllowedTabsPeople_DashUsers = (user: User | null) => {
  let dashUsersTabs: MenuOption[] = [];

  if (userCanView_Instructors(user)) {
    dashUsersTabs.push({
      title: 'Instructors',
      url: '/admin/dash-users/instructors'
    });
  }
  
  if (userCanView_AdminUser(user)) {
    dashUsersTabs.push({ title: 'Admins', url: '/admin/dash-users/admins' });
  }

  return dashUsersTabs;
};

export const getAllowedTabsPeople_SFCUsers = (user: User | null) => {
  let dashUsersTabs: MenuOption[] = [];
  if (user?.isInstructor) return [];

  if (userCanView_ParentUser(user)) {
    dashUsersTabs.push({ title: 'Parents', url: '/admin/sfc-users/parents' });
  }

  if (userCanView_Children(user)) {
    dashUsersTabs.push({ title: 'Children', url: '/admin/sfc-users/children' });
  }

  if (userCanView_Allergies(user)) {
    dashUsersTabs.push({
      title: 'Allergies',
      url: '/admin/allergies'
    });
  }

  if (userCanView_Waviers(user)) {
    dashUsersTabs.push({
      title: 'Waivers',
      url: '/admin/waivers'
    });
  }
  return dashUsersTabs;
};

export const getAllowedTabsFinance = (user: User | null) => {
  let financeTabs: MenuOption[] = [];
  // if (userCanAdmin_Invoices(user)) {
  //   financeTabs.push({
  //     title: 'KPI Reports',
  //     url: '/admin/finance/powerBi'
  //   });
  // }
  if (userCanAdmin_Invoices(user)) {
    financeTabs.push({
      title: 'Invoices',
      url: '/admin/finance/invoices'
    });
  }

  // if (userCanView_CurrentEntries(user) && !user?.isInstructor) {
  //   financeTabs.push({
  //     title: 'Current Entries',
  //     url: '/admin/finance/current-entries'
  //   });
  // }

  if (userCanAdmin_Expenses(user)) {
    financeTabs.push({
      title: 'Expenses',
      url: '/admin/finance/expenses'
    });
  }

  if (userCanAdmin_Receipts(user)) {
    financeTabs.push({
      title: 'Receipts',
      url: '/admin/finance/receipts'
    });
  }

  if (userCanViewEdit_Payments(user)) {
    financeTabs.push({
      title: 'Payments',
      url: '/admin/finance/payments'
    });
  }
  return financeTabs;
};

export const getAllowedTabsTimesheets = (user: User | null) => {
  if (user?.user_type_id === userTypes.global_admin) return [];

  let timesheetsTabs: MenuOption[] = [];

  if (userCanViewMasterTimesheet(user)) {
    timesheetsTabs.push({
      title: 'Master Timesheet',
      url: '/admin/timesheets/master-timesheet'
    });
  }

  timesheetsTabs.push(
      {
        title: `Current Entries`,
        url: '/admin/timesheets/current-timesheet'
      },
      {
        title: 'Past Timesheets',
        url: '/admin/timesheets/past-timesheets'
      }
  );

  if (isLocal(user?.user_type_id!)) {
    timesheetsTabs.push({
      title: 'Overrides',
      url: '/admin/timesheets/timesheet-overrides'
    });
  }

  return timesheetsTabs;
};

export const getAllowedTabsOrders = (user: User | null) => {
  let ordersTabs: MenuOption[] = [];

  if (userCanViewRemoveSubmit_Orders(user)) {
    ordersTabs.push({
      title: 'Orders',
      url: '/admin/orders/orders'
    });
  }

  if (userCanAdmin_Refunds(user)) {
    ordersTabs.push({
      title: 'Refunds',
      url: '/admin/orders/refunds'
    });
  }

  if (userCanView_Scholarships(user)) {
    ordersTabs.push({
      title: 'Scholarships',
      url: '/admin/orders/scholarships'
    });
  }

  if (userCanAdmin_Discounts(user)) {
    ordersTabs.push({
      title: 'Discounts',
      url: '/admin/orders/discounts'
    });
  }

  if (userCanView_PromotionalCodes(user)) {
    ordersTabs.push({
      title: 'Promotional Codes',
      url: '/admin/orders/promotional-codes'
    });
  }
  return ordersTabs;
};
